const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let _logoUrlAbsoluta = null;
let _encabezadoImpresion = null;

export const ENCABEZADO_IMPRESION_DEFAULT = {
  razon_social: '',
  cuit: '',
  direccion: '',
  localidad: '',
  telefono: '',
  email: '',
  condicion_iva: '',
  incluir: {
    razon_social: true,
    cuit: true,
    direccion: true,
    localidad: true,
    telefono: true,
    email: true,
    condicion_iva: true,
  },
};

export function normalizarEncabezadoImpresion(raw) {
  const base = { ...ENCABEZADO_IMPRESION_DEFAULT, incluir: { ...ENCABEZADO_IMPRESION_DEFAULT.incluir } };
  if (!raw || typeof raw !== 'object') return base;
  const incluir = { ...base.incluir, ...(raw.incluir || {}) };
  return {
    razon_social: String(raw.razon_social ?? '').trim(),
    cuit: String(raw.cuit ?? '').trim(),
    direccion: String(raw.direccion ?? '').trim(),
    localidad: String(raw.localidad ?? '').trim(),
    telefono: String(raw.telefono ?? '').trim(),
    email: String(raw.email ?? '').trim(),
    condicion_iva: String(raw.condicion_iva ?? '').trim(),
    incluir,
  };
}

export function resolveLogoUrl(relativeUrl) {
  if (!relativeUrl) return null;
  if (relativeUrl.startsWith('http') || relativeUrl.startsWith('data:')) return relativeUrl;
  const base = API_URL.replace(/\/api\/?$/, '');
  return `${base}${relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`}`;
}

export function setPrintLogoUrl(url) {
  _logoUrlAbsoluta = url ? resolveLogoUrl(url) : null;
}

export function getPrintLogoUrl() {
  return _logoUrlAbsoluta;
}

export function setPrintEncabezado(encabezado) {
  _encabezadoImpresion = normalizarEncabezadoImpresion(encabezado);
}

export function getPrintEncabezado() {
  return _encabezadoImpresion || normalizarEncabezadoImpresion(null);
}

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

function buildEmpresaDatosHtml(encabezado) {
  const e = normalizarEncabezadoImpresion(encabezado);
  const lineas = [];
  if (e.incluir.razon_social && e.razon_social) {
    lineas.push(`<div class="print-empresa-nombre">${esc(e.razon_social)}</div>`);
  }
  if (e.incluir.cuit && e.cuit) lineas.push(`<div>CUIT: ${esc(e.cuit)}</div>`);
  if (e.incluir.condicion_iva && e.condicion_iva) lineas.push(`<div>${esc(e.condicion_iva)}</div>`);
  if (e.incluir.direccion && e.direccion) lineas.push(`<div>${esc(e.direccion)}</div>`);
  if (e.incluir.localidad && e.localidad) lineas.push(`<div>${esc(e.localidad)}</div>`);
  if (e.incluir.telefono && e.telefono) lineas.push(`<div>Tel: ${esc(e.telefono)}</div>`);
  if (e.incluir.email && e.email) lineas.push(`<div>${esc(e.email)}</div>`);
  return lineas.length ? `<div class="print-empresa-datos">${lineas.join('')}</div>` : '';
}

function buildDocMetaHtml({ titulo, fecha, numeroDocumento, numeroLabel }) {
  const partes = [];
  if (titulo) partes.push(`<div class="print-doc-titulo">${esc(titulo)}</div>`);
  if (numeroDocumento != null && numeroDocumento !== '') {
    partes.push(`<div class="print-doc-meta"><strong>${esc(numeroLabel)}</strong> ${esc(numeroDocumento)}</div>`);
  }
  if (fecha) partes.push(`<div class="print-doc-meta"><strong>Fecha:</strong> ${esc(fecha)}</div>`);
  return partes.length ? `<div class="print-header-doc">${partes.join('')}</div>` : '';
}

/**
 * Encabezado de impresión: logo + datos empresa a la izquierda; título/fecha/número a la derecha.
 * Acepta (titulo, logoUrl) legacy o un objeto de opciones.
 */
export function buildPrintHeaderHtml(arg1, arg2, arg3 = {}) {
  let titulo;
  let logoUrl;
  let opts = arg3;

  if (typeof arg1 === 'object' && arg1 !== null && !Array.isArray(arg1)) {
    ({ titulo, logoUrl, ...opts } = arg1);
  } else {
    titulo = arg1;
    logoUrl = arg2;
  }

  const {
    encabezado = getPrintEncabezado(),
    fecha,
    numeroDocumento,
    numeroLabel = 'N°',
  } = opts;

  const logo = logoUrl || _logoUrlAbsoluta;
  const logoImg = logo ? `<img src="${logo}" alt="Logo" class="print-logo" />` : '';
  const empresaDatos = buildEmpresaDatosHtml(encabezado);
  const docMeta = buildDocMetaHtml({ titulo, fecha, numeroDocumento, numeroLabel });

  const brandInner = logoImg || empresaDatos
    ? `<div class="print-header-brand">${logoImg}${empresaDatos}</div>`
    : '';

  return `
    <div class="print-header">
      ${brandInner}
      ${docMeta}
    </div>
  `;
}

export const estilosPrintHeaderLayout = `
  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    border-bottom: 1px solid #333;
    padding-bottom: 8px;
    margin-bottom: 10px;
  }
  .print-header-brand {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }
  .print-logo {
    flex-shrink: 0;
    max-height: 56px;
    max-width: 120px;
    object-fit: contain;
    display: block;
    margin: 0;
  }
  .print-empresa-datos {
    font-size: 9px;
    line-height: 1.4;
    color: #333;
  }
  .print-empresa-nombre {
    font-size: 11px;
    font-weight: bold;
    margin-bottom: 3px;
  }
  .print-header-doc {
    text-align: right;
    flex-shrink: 0;
    align-self: flex-start;
    min-width: 140px;
  }
  .print-doc-titulo {
    font-size: 13px;
    font-weight: bold;
    margin: 0 0 4px 0;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #222;
  }
  .print-doc-meta {
    font-size: 9px;
    margin: 2px 0;
    color: #333;
  }
`;

export const estilosPrintHeader = `
  ${estilosPrintHeaderLayout}
  .print-header { margin-bottom: 10px; }
  .print-logo { max-height: 56px; max-width: 120px; }
  .print-doc-titulo { font-size: 13px; }
`;

export const estilosPrintHeaderInforme = `
  ${estilosPrintHeaderLayout}
  .print-header {
    border-bottom: 2px solid #333;
    padding-bottom: 14px;
    margin-bottom: 20px;
  }
  .print-logo { max-height: 72px; max-width: 140px; }
  .print-empresa-datos { font-size: 10px; }
  .print-empresa-nombre { font-size: 12px; }
  .print-doc-titulo { font-size: 18px; }
  .print-doc-meta { font-size: 10px; }
`;

export const estilosPrintHeaderListado = `
  ${estilosPrintHeaderLayout}
  .print-header { margin-bottom: 14px; }
  .print-logo { max-height: 64px; max-width: 130px; }
  .print-doc-titulo { font-size: 16px; }
  .print-doc-meta { font-size: 10px; }
`;
